'use client';

import { publicPagesContent } from '@/lib/i18n/public-pages';
import { useLocale } from '@/lib/i18n/use-locale';
import Link from 'next/link';

export function AboutContent() {
  const { locale } = useLocale();
  const content = publicPagesContent[locale].about;

  return (
    <main className='mx-auto max-w-6xl px-4 pb-20 pt-12'>
      <section className='relative overflow-hidden rounded-3xl border border-blue-100/70 bg-white/90 p-10 shadow-xl shadow-blue-100/40 backdrop-blur dark:border-blue-900/40 dark:bg-gray-900/80 dark:shadow-none'>
        <div className='pointer-events-none absolute -right-24 -top-20 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl' />
        <div className='pointer-events-none absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl' />
        <p className='text-xs font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-300'>
          {content.subtitle}
        </p>
        <h1 className='mt-4 text-4xl font-semibold text-gray-900 dark:text-white sm:text-5xl'>
          {content.title}
        </h1>
        <div className='mt-8 flex flex-wrap gap-4'>
          <Link
            href='/faq'
            className='rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:-translate-y-0.5 hover:bg-blue-700'
          >
            {content.faqCta}
          </Link>
          <Link
            href='/abuse'
            className='rounded-full border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-700 transition hover:border-gray-300 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:text-white'
          >
            {content.abuseCta}
          </Link>
        </div>
      </section>

      <section className='mt-12 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]'>
        <div className='rounded-2xl border border-gray-200/70 bg-white p-8 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
          <h2 className='text-2xl font-semibold text-gray-900 dark:text-white'>
            {content.missionTitle}
          </h2>
          <div className='mt-4 space-y-4 text-lg text-gray-700 dark:text-gray-300'>
            {content.missionParagraphs.map(paragraph => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
        <div className='rounded-2xl border border-blue-200/70 bg-gradient-to-br from-blue-50 to-white p-8 shadow-lg shadow-blue-100/60 dark:border-blue-900/50 dark:from-blue-900/20 dark:to-gray-900 dark:shadow-none'>
          <h2 className='text-2xl font-semibold text-gray-900 dark:text-white'>
            {content.privacyTitle}
          </h2>
          <ul className='mt-4 space-y-3 text-gray-700 dark:text-gray-300'>
            {content.privacyBullets.map(bullet => (
              <li key={bullet} className='flex items-start gap-3'>
                <span className='mt-1 text-green-600 dark:text-green-400'>✓</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
          <p className='mt-6 text-sm text-gray-600 dark:text-gray-400'>
            {content.privacyNotePrefix}{' '}
            <Link
              href='/privacy'
              className='font-semibold text-blue-600 hover:underline dark:text-blue-300'
            >
              {content.privacyLinkText}
            </Link>{' '}
            {content.privacyNoteSuffix}
          </p>
        </div>
      </section>

      <section className='mt-12'>
        <div className='mb-6 flex items-center justify-between'>
          <h2 className='text-2xl font-semibold text-gray-900 dark:text-white'>
            {content.whyTitle}
          </h2>
        </div>
        <div className='grid gap-6 md:grid-cols-2'>
          {content.features.map(feature => (
            <div
              key={feature.title}
              className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'
            >
              <div className='text-4xl'>{feature.emoji}</div>
              <h3 className='mt-3 text-lg font-semibold text-gray-900 dark:text-white'>
                {feature.title}
              </h3>
              <p className='mt-2 text-gray-600 dark:text-gray-300'>{feature.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className='mt-12'>
        <div className='relative overflow-hidden rounded-3xl border border-gray-200/70 bg-white p-8 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
          <div className='pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl' />
          <div className='pointer-events-none absolute -bottom-20 left-16 h-48 w-48 rounded-full bg-emerald-500/10 blur-3xl' />
          <div className='grid gap-8 lg:grid-cols-[0.9fr_1.1fr]'>
            <div>
              <h2 className='text-2xl font-semibold text-gray-900 dark:text-white'>
                {content.techTitle}
              </h2>
              <p className='mt-3 text-gray-600 dark:text-gray-300'>{content.techIntro}</p>
              <div className='mt-6 flex flex-wrap gap-2'>
                {content.techStack.slice(0, 4).map(tech => (
                  <span
                    key={tech.name}
                    className='rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200'
                  >
                    {tech.name}
                  </span>
                ))}
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
              {content.techStack.map(tech => (
                <div
                  key={tech.name}
                  className='group rounded-2xl border border-gray-100 bg-white/90 p-4 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-gray-800 dark:bg-gray-950'
                >
                  <div className='mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 transition group-hover:scale-105 dark:bg-blue-950/60 dark:text-blue-200'>
                    {tech.name.charAt(0)}
                  </div>
                  <div className='mt-3 text-base font-semibold text-gray-900 dark:text-white'>
                    {tech.name}
                  </div>
                  <div className='mt-1 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400'>
                    {tech.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
